import { useEffect, useState } from 'react'
import { Modal, Button, Table } from 'react-bootstrap'
import { getRequestOptions } from '../helpers/Fetchwrapper'

interface ComplianceLine {
	idSalesOrderRows: number
	productName: string
	packSize: string
	quantity: number
	unitName: string
	rate: number
	amount: number
	complianceRate: string
}

const numberFormat = (value: number) => {
	return value.toLocaleString('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

function ComplianceInvoiceModal({
	show,
	invoiceId,
	onClose,
}: {
	show: boolean
	invoiceId: number | null
	onClose: () => void
}) {
	const [lines, setLines] = useState<ComplianceLine[]>([])
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!show || !invoiceId) {
			setLines([])
			return
		}

		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'sales-invoice/' + invoiceId, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const items = resp.data?.items || []
				setLines(items.map((item: any) => {
					const orderRow = item.sales_order_row || item.salesOrderRow || {}
					const product = orderRow.product || {}
					const packUnit = product.item?.unit?.unit_name || ''
					const quantity = parseFloat(item.delivered_quantity ?? item.deliveredQuantity ?? 0)

					return {
						idSalesOrderRows: orderRow.id_sales_order_rows || orderRow.idSalesOrderRows,
						productName: orderRow.alternate_name?.alternate_name
							|| orderRow.alternateName?.alternate_name
							|| product.product_name
							|| product.productName
							|| '',
						packSize: `${product.packing || ''} ${packUnit}`.trim(),
						quantity,
						unitName: product.unit?.unit_name || product.unit?.unitName || '',
						rate: parseFloat(orderRow.unit_rate ?? orderRow.unitRate ?? 0),
						amount: parseFloat(item.delivered_amount ?? item.deliveredAmount ?? 0),
						complianceRate: '',
					}
				}))
				setLoading(false)
			})
			.catch(() => {
				setLines([])
				setLoading(false)
			})
	}, [show, invoiceId])

	const complianceAmount = (line: ComplianceLine) => {
		const rate = parseFloat(line.complianceRate)
		if (line.complianceRate === '' || isNaN(rate)) {
			return null
		}
		return parseFloat((rate * line.quantity).toFixed(2))
	}

	const complianceTotal = lines.reduce((sum, line) => {
		const amount = complianceAmount(line)
		return amount === null ? sum : sum + amount
	}, 0)

	const canPrint = lines.length > 0 && lines.every((line) => {
		const rate = parseFloat(line.complianceRate)
		return line.complianceRate !== '' && !isNaN(rate) && rate >= 0
	})

	const handleRateChange = (index: number, value: string) => {
		setLines((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], complianceRate: value }
			return updated
		})
	}

	const handlePrint = () => {
		if (!invoiceId || !canPrint) {
			return
		}

		const params = new URLSearchParams()
		lines.forEach((line) => {
			params.append(`complianceRates[${line.idSalesOrderRows}]`, line.complianceRate)
		})
		window.open(
			`${process.env.REACT_APP_BACKEND}invoiceDownload/${invoiceId}?${params.toString()}`,
			'_blank',
			'noopener,noreferrer'
		)
	}

	return (
		<Modal show={show} onHide={onClose} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>Compliance Invoice</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loading && <div>Loading...</div>}
				{!loading && (
					<Table size="sm" bordered hover responsive>
						<thead>
							<tr>
								<th>SN</th>
								<th>Product</th>
								<th>Pack Size</th>
								<th className="text-end">Quantity</th>
								<th>Unit</th>
								<th className="text-end">Rate</th>
								<th className="text-end">Amount</th>
								<th className="text-end">Compliance Rate</th>
								<th className="text-end">Compliance Amount</th>
							</tr>
						</thead>
						<tbody>
							{lines.map((line, index) => {
								const amount = complianceAmount(line)
								return (
									<tr key={line.idSalesOrderRows || index}>
										<td>{index + 1}</td>
										<td>{line.productName}</td>
										<td>{line.packSize}</td>
										<td className="text-end">{line.quantity}</td>
										<td>{line.unitName}</td>
										<td className="text-end">{numberFormat(line.rate)}</td>
										<td className="text-end">{numberFormat(line.amount)}</td>
										<td style={{ minWidth: '140px' }}>
											<input
												type="number"
												min={0}
												step="0.01"
												className="form-control form-control-sm text-end"
												value={line.complianceRate}
												onChange={(event) => handleRateChange(index, event.target.value)}
											/>
										</td>
										<td className="text-end">{amount === null ? '' : numberFormat(amount)}</td>
									</tr>
								)
							})}
							{lines.length === 0 && (
								<tr>
									<td colSpan={9} className="text-center text-muted">No products found on this invoice.</td>
								</tr>
							)}
						</tbody>
						{lines.length > 0 && (
							<tfoot>
								<tr>
									<th colSpan={8} className="text-end">Compliance Total Amount</th>
									<th className="text-end">{numberFormat(complianceTotal)}</th>
								</tr>
							</tfoot>
						)}
					</Table>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
				<Button variant="primary" size="sm" onClick={handlePrint} disabled={!canPrint}>Print</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default ComplianceInvoiceModal
